import { Autocomplete, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useFinanceInvoices } from "@/hooks/useInvoices";
import { useClientPurchases } from "@/hooks/usePurchases";

/** SourceType on the API. Only the two a person can meaningfully pick are offered. */
export const SOURCE_PURCHASE = 0;
export const SOURCE_INVOICE = 1;

export interface SourceOption {
  id: number;
  label: string;
  organizationName: string;
  groupName: string;
}

/** How many matches the dropdown shows at once. Anything beyond this is reachable by searching. */
const PAGE = 20;

/**
 * How long a search result stays fresh. Backspacing to a search you already ran then costs nothing
 * — without it, React Query serves the cache instantly but still refetches in the background, so
 * every revisit was a request. Invoices don't change second to second, and any mutation invalidates
 * these queries anyway, so staleness here can't hide a change someone just made.
 */
const SEARCH_STALE_MS = 30_000;

/**
 * The two-step source picker: pick a kind, then search within it.
 *
 * Everything is server-side: with a thousand invoices the dropdown holds twenty, and typing narrows
 * them in SQL (invoice number, contact, email) rather than filtering a list in the browser.
 *
 * Shared by the New Journal Entry screen, which needs the selected row's org and group, and the
 * Journal Entries filter, which only needs the ids. Both call it because it is the same question
 * asked twice; the second copy is where the two would have drifted.
 *
 * Both list hooks are called on every render — rules of hooks — but only the selected kind fetches.
 */
export default function SourcePicker({
  sourceType,
  sourceId,
  onChange,
  size = "small",
  entityLabel = "Select",
}: {
  sourceType: number | "";
  sourceId: number | null;
  /** `selected` is the resolved row, so a caller can read its org / group without re-fetching. */
  onChange: (
    sourceType: number | "",
    sourceId: number | null,
    selected: SourceOption | null,
  ) => void;
  size?: "small" | "medium";
  entityLabel?: string;
}) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  // Remembered separately from the options so a selection survives a later search that doesn't
  // return it — otherwise the box would appear empty while the filter was still applied.
  const [picked, setPicked] = useState<SourceOption | null>(null);

  // Debounced like the Blotter's search: typing "INV-2026-08" is one query, not eleven.
  useEffect(() => {
    const t = setTimeout(() => setSearch(input), 300);
    return () => clearTimeout(t);
  }, [input]);

  const { data: invoicePage, isFetching: invoicesFetching } = useFinanceInvoices(
    { search: search || undefined, page: 1, limit: PAGE },
    { enabled: sourceType === SOURCE_INVOICE, staleTime: SEARCH_STALE_MS },
  );
  const { data: purchasePage, isFetching: purchasesFetching } = useClientPurchases(
    { search: search || undefined, page: 1, limit: PAGE },
    { enabled: sourceType === SOURCE_PURCHASE, staleTime: SEARCH_STALE_MS },
  );

  const loading =
    (sourceType === SOURCE_INVOICE && invoicesFetching) ||
    (sourceType === SOURCE_PURCHASE && purchasesFetching);
  const total =
    sourceType === SOURCE_INVOICE
      ? (invoicePage?.total ?? 0)
      : sourceType === SOURCE_PURCHASE
        ? (purchasePage?.total ?? 0)
        : 0;

  const options: SourceOption[] = useMemo(() => {
    if (sourceType === SOURCE_INVOICE) {
      return (invoicePage?.data ?? []).map((i) => ({
        id: i.id,
        label: i.invoiceNumber ?? `Invoice #${i.id}`,
        organizationName: i.organizationName ?? "—",
        groupName: i.groupName ?? "—",
      }));
    }
    if (sourceType === SOURCE_PURCHASE) {
      return (purchasePage?.data ?? []).map((p) => ({
        id: p.id,
        label: p.purchaseNumber ?? `Purchase #${p.id}`,
        organizationName: p.organizationName ?? "—",
        groupName: p.groupName ?? "—",
      }));
    }
    return [];
  }, [sourceType, invoicePage, purchasePage]);

  // Pin the current selection into the list. Without this, picking invoice #500 and then typing a
  // search that excludes it would blank the field while sourceId still filtered the blotter.
  const withPicked = useMemo(() => {
    if (!picked || options.some((o) => o.id === picked.id)) return options;
    return [picked, ...options];
  }, [options, picked]);

  const selected = withPicked.find((o) => o.id === sourceId) ?? null;
  const hiddenMatches = Math.max(0, total - options.length);

  return (
    <>
      <TextField
        select
        size={size}
        label="Source"
        value={sourceType}
        onChange={(e) => {
          // Changing the kind invalidates whatever was picked under the old one.
          const next = e.target.value === "" ? "" : Number(e.target.value);
          setInput("");
          setSearch("");
          setPicked(null);
          onChange(next, null, null);
        }}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">Any</MenuItem>
        <MenuItem value={SOURCE_INVOICE}>Invoice</MenuItem>
        <MenuItem value={SOURCE_PURCHASE}>Client purchase</MenuItem>
      </TextField>
      <Autocomplete
        size={size}
        disabled={sourceType === ""}
        options={withPicked}
        value={selected}
        loading={loading}
        onChange={(_, v) => {
          setPicked(v);
          onChange(sourceType, v?.id ?? null, v);
        }}
        onInputChange={(_, v, reason) => {
          // "reset" fires when a value is selected — echoing the label back as a search would throw
          // away the list the user just picked from.
          if (reason === "input") setInput(v);
        }}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        sx={{ minWidth: 220 }}
        noOptionsText={loading ? "Searching…" : "No matches"}
        renderInput={(params) => (
          <TextField
            {...params}
            label={entityLabel}
            // Say when the list is truncated, so "not there" is distinguishable from "type more".
            helperText={
              hiddenMatches > 0 ? `${hiddenMatches} more — keep typing to narrow` : undefined
            }
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress aria-label="Loading" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </>
  );
}
